import { Column, ColumnApi, ColumnEvent, GridApi, IAfterGuiAttachedParams, IComponent, IHeader, IHeaderParams } from "ag-grid-community";
import { ModuleManager } from "src/shared/models/module-manager";

export default class GroupHeaderComp implements IHeader, IComponent<IHeaderParams> {
    private gui: HTMLElement;
    private column: Column;
    private api: GridApi;
    sortAsc: HTMLSpanElement = null;
    sortDesc: HTMLSpanElement = null;


    private onSortChanged: (e: ColumnEvent) => void;
    private onGlobalSortChanged: (e: any) => void;
    private onFilterChanged: (e: ColumnEvent) => void;

    init(params: IHeaderParams) {
        const moduleManager: ModuleManager = params.context.moduleManager;
        this.gui = document.createElement('div');
        this.api = params.api;
        this.column = params.column;
        this.gui.classList.add('ag-cell-label-container');

        // menu (copied from ag-grid)
        // when writing custom components we have to re-implement the default features
        const menu: HTMLSpanElement = document.createElement('span');
        menu.setAttribute('ref', 'eMenu');
        menu.classList.add('ag-header-icon', 'ag-header-cell-menu-button');
        menu.style.opacity = '0';
        menu.style.transition = 'opacity 0.2s ease 0s, border 0.2s ease 0s';
        this.gui.appendChild(menu);

        if (params.enableMenu) {
            this.gui.addEventListener('mouseenter', (e) => { menu.style.opacity = '1'; });
            this.gui.addEventListener('mouseleave', (e) => { menu.style.opacity = '0'; });
            menu.addEventListener('click', (e) => params.showColumnMenu(menu));
        }

        if (params.context.moduleManager.container.config?.isCxOne){
            const menuIcon: HTMLSpanElement = document.createElement('span');
            const menuSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            menuSvg.classList.add('icon-20', 'fill-iconMenu');
            menuSvg.setAttribute('stroke', '#D2D8DB');
            menuSvg.setAttribute('viewBox', '0 0 16 16');
            menuIcon.appendChild(menuSvg);

            const menuSvgPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            menuSvgPath.setAttribute('d', 'M6.166 13.5h3.666v-1.834h-3.666v1.834zM-0.25 2.5v1.834h16.5v-1.834h-16.5zM2.5 8.916h11v-1.834h-11v1.834z');
            menuSvgPath.style.strokeWidth = '0.25px';
            menuSvg.appendChild(menuSvgPath);
            menuIcon.setAttribute('unselectable', 'on');
            menu.appendChild(menuIcon);
        } else {
            const menuIcon: HTMLSpanElement = document.createElement('span');
            menuIcon.setAttribute('unselectable', 'on');
            menuIcon.classList.add('ag-icon', 'ag-icon-menu');
            menu.appendChild(menuIcon);
        }

        const labelContainer: HTMLDivElement = document.createElement('div');
        labelContainer.classList.add('ag-header-cell-label');
        labelContainer.setAttribute('unselectable', 'on');
        if (params.enableSorting)
            labelContainer.addEventListener('click', (e) => { params.progressSort(e.shiftKey); });
        this.gui.appendChild(labelContainer);

        const labelText: HTMLSpanElement = document.createElement('span');
        labelText.setAttribute('ref', 'eText');
        labelText.setAttribute('unselectable', 'on');
        labelText.classList.add('ag-header-cell-text');

        const writeAccess = moduleManager?.hasWriteAccess() ?? false;
        const readGroupAccess = moduleManager?.hasReadGroupAccess() ?? false;

        // parent group
        if ((writeAccess || readGroupAccess) &&
            moduleManager.moduleSettings.selectedGroupLevel < moduleManager.moduleSettings.maxGroupLevel) {
            const parentGroup = document.createElement('a');
            if (moduleManager.container.config?.isCxOne) {
                parentGroup.classList.add('mr-2');
                const svgParent = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                svgParent.classList.add('icon-16', 'fill-cxoneBlue');
                svgParent.setAttribute('stroke', '#007cbe');
                svgParent.setAttribute('viewBox', '0 0 16 16');
                parentGroup.appendChild(svgParent);

                const svgParentUse = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                svgParentUse.setAttribute('d', 'M7.004 4.228l-6.765 6.418c-0.147 0.144-0.239 0.344-0.239 0.566s0.091 0.422 0.239 0.566l0 0c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001 6.367-6.038 6.767 6.038c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001c0.147-0.144 0.239-0.344 0.239-0.566s-0.091-0.422-0.239-0.566l-0-0-7.165-6.418c-0.16-0.145-0.374-0.233-0.608-0.233-0.039 0-0.078 0.003-0.117 0.007l0.005-0c-0.036-0.004-0.079-0.006-0.121-0.006-0.28 0-0.541 0.087-0.755 0.235l0.004-0.003z');
                svgParentUse.setAttribute('stroke-linecap', 'round');
                svgParent.appendChild(svgParentUse);
                labelContainer.appendChild(parentGroup);

                this.setCxoneHeaderCell(moduleManager, labelText);
            } else {
                parentGroup.innerText = moduleManager.moduleSettings.parentGroupLevelName;
                labelContainer.appendChild(parentGroup);

                const sep = document.createElement('span');
                sep.classList.add('mx-1');
                sep.innerText = " - ";
                labelContainer.appendChild(sep);

                labelText.innerText = moduleManager.moduleSettings.selectedGroupName;
            }
            parentGroup.style.cursor = 'pointer';
            parentGroup.addEventListener('click', (e) => {
                moduleManager.drillDown();
                e.stopPropagation();
            });
        } else {
            if (moduleManager.container.config?.isCxOne) { //ensures it exists on projections
                this.setCxoneHeaderCell(moduleManager, labelText);
            }
            else {
                labelText.innerText = moduleManager.moduleSettings.selectedGroupLevelName;
            }
        }

        labelContainer.appendChild(labelText);

        // filter
        const filter: HTMLSpanElement = document.createElement('span');
        filter.setAttribute('ref', 'eFilter');
        filter.classList.add('ag-header-icon', 'ag-filter-icon', 'ag-header-label-icon');
        filter.classList.toggle('ag-hidden', !params.column.isFilterActive());
        labelContainer.appendChild(filter);

        const filterIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        filterIcon.classList.add('icon-20', 'fill-iconMenu');
        filterIcon.setAttribute('viewBox', '0 0 16 16');
        filter.appendChild(filterIcon);

        const filterSvgUse = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        filterSvgUse.setAttribute('d', 'M13.44 1.6l-4.64 5.84v5.52l-1.6 0.88v-6.4l-0.32-0.4-4.32-5.44h10.88zM15.36 0h-14.72c-0.24 0-0.48 0.16-0.56 0.4-0.16 0.24-0.080 0.56 0.080 0.72l5.44 6.88v7.2c0 0.24 0.24 0.56 0.48 0.72 0.080 0.080 0.24 0.080 0.32 0.080s0.16 0 0.32-0.080l3.2-1.68c0.24-0.080 0.56-0.32 0.56-0.64v-5.6l5.44-6.88c0.16-0.24 0.16-0.48 0.080-0.72-0.16-0.24-0.4-0.4-0.64-0.4v0z');
        filterSvgUse.setAttribute('stroke-linecap', 'round');
        filterIcon.appendChild(filterSvgUse);

        // sort order
        const sortOrder: HTMLSpanElement = document.createElement('span');
        sortOrder.setAttribute('ref', 'eSortOrder');
        sortOrder.classList.add('ag-header-icon', 'ag-sort-order');
        const sortOrderVal = this.getSortOrder(params.api, params.columnApi);
        sortOrder.classList.toggle('ag-hidden', sortOrderVal <= 0);
        sortOrder.innerText = sortOrderVal.toString();
        labelContainer.appendChild(sortOrder);


        const sort = params.column.getSort();
        // sort asc
        if (params.context.moduleManager.container.config?.isCxOne){
            this.sortAsc = document.createElement('span');
            this.sortAsc.setAttribute('ref', 'eSortAsc');
            this.sortAsc.classList.add('sortIcons', 'ag-header-icon', 'ag-sort-ascending-icon');
            this.sortAsc.style.marginLeft = '0.5rem';
            labelContainer.appendChild(this.sortAsc);


            const sortSvgTop = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            sortSvgTop.classList.add('icon-10', 'fill-cxoneBlue');
            sortSvgTop.setAttribute('stroke', '#007cbe');
            sortSvgTop.setAttribute('viewBox', '0 0 16 16');
            this.sortAsc.appendChild(sortSvgTop);

            const sortSvgTopUse = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            sortSvgTopUse.setAttribute('d', 'M7.004 4.228l-6.765 6.418c-0.147 0.144-0.239 0.344-0.239 0.566s0.091 0.422 0.239 0.566l0 0c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001 6.367-6.038 6.767 6.038c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001c0.147-0.144 0.239-0.344 0.239-0.566s-0.091-0.422-0.239-0.566l-0-0-7.165-6.418c-0.16-0.145-0.374-0.233-0.608-0.233-0.039 0-0.078 0.003-0.117 0.007l0.005-0c-0.036-0.004-0.079-0.006-0.121-0.006-0.28 0-0.541 0.087-0.755 0.235l0.004-0.003z');
            sortSvgTopUse.setAttribute('stroke-linecap', 'round');
            sortSvgTop.appendChild(sortSvgTopUse);

            const sortSvgBottom= document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            sortSvgBottom.classList.add('icon-10', 'fill-sort');
            sortSvgBottom.setAttribute('stroke', '#93A4AA');
            sortSvgBottom.setAttribute('viewBox', '0 0 16 16');
            this.sortAsc.appendChild(sortSvgBottom);

            const sortSvgBottomUse= document.createElementNS('http://www.w3.org/2000/svg', 'path');
            sortSvgBottomUse.setAttribute('d', 'M7.004 11.772l-6.765-6.418c-0.147-0.144-0.239-0.344-0.239-0.566s0.091-0.422 0.239-0.566l0-0c0.158-0.141 0.368-0.227 0.597-0.227s0.439 0.086 0.598 0.228l-0.001-0.001 6.367 6.038 6.767-6.038c0.158-0.141 0.368-0.227 0.597-0.227s0.439 0.086 0.598 0.228l-0.001-0.001c0.147 0.144 0.239 0.344 0.239 0.566s-0.091 0.422-0.239 0.566l-0 0-7.165 6.418c-0.16 0.145-0.374 0.233-0.608 0.233-0.039 0-0.078-0.003-0.117-0.007l0.005 0c-0.036 0.004-0.079 0.006-0.121 0.006-0.28 0-0.541-0.087-0.755-0.235l0.004 0.003z');
            sortSvgBottomUse.setAttribute('stroke-linecap', 'round');
            sortSvgBottom.appendChild(sortSvgBottomUse);
            this.sortAsc.classList.toggle('ag-hidden', sort !== 'asc');
        }
        else {
            this.sortAsc = document.createElement('span');
            this.sortAsc.setAttribute('ref', 'eSortAsc');

            this.sortAsc.classList.add('ag-header-icon', 'ag-sort-ascending-icon');
            this.sortAsc.classList.toggle('ag-hidden', sort !== 'asc');
            labelContainer.appendChild(this.sortAsc);

            const sortAscIcon: HTMLSpanElement = document.createElement('span');
            sortAscIcon.setAttribute('unselectable', 'on');
            sortAscIcon.classList.add('ag-icon', 'ag-icon-asc');
            this.sortAsc.appendChild(sortAscIcon);
        }
        // sort desc

        if (params.context.moduleManager.container.config?.isCxOne){
            this.sortDesc = document.createElement('span');
            this.sortDesc.setAttribute('ref', 'eSortAsc');
            this.sortDesc.classList.add('sortIcons');
            this.sortDesc.style.marginLeft = '0.5rem';
            labelContainer.appendChild(this.sortDesc);


            const sortSvgTop = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            sortSvgTop.classList.add('icon-10', 'fill-sort');
            sortSvgTop.setAttribute('stroke', '#93A4AA');
            sortSvgTop.setAttribute('viewBox', '0 0 16 16');
            this.sortDesc.appendChild(sortSvgTop);

            const sortSvgTopUse = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            sortSvgTopUse.setAttribute('d', 'M7.004 4.228l-6.765 6.418c-0.147 0.144-0.239 0.344-0.239 0.566s0.091 0.422 0.239 0.566l0 0c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001 6.367-6.038 6.767 6.038c0.158 0.141 0.368 0.227 0.597 0.227s0.439-0.086 0.598-0.227l-0.001 0.001c0.147-0.144 0.239-0.344 0.239-0.566s-0.091-0.422-0.239-0.566l-0-0-7.165-6.418c-0.16-0.145-0.374-0.233-0.608-0.233-0.039 0-0.078 0.003-0.117 0.007l0.005-0c-0.036-0.004-0.079-0.006-0.121-0.006-0.28 0-0.541 0.087-0.755 0.235l0.004-0.003z');
            sortSvgTopUse.setAttribute('stroke-linecap', 'round');
            sortSvgTop.appendChild(sortSvgTopUse);

            const sortSvgBottom= document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            sortSvgBottom.classList.add('icon-10', 'fill-cxoneBlue');
            sortSvgBottom.setAttribute('stroke', '#007cbe');
            sortSvgBottom.setAttribute('viewBox', '0 0 16 16');
            this.sortDesc.appendChild(sortSvgBottom);

            const sortSvgBottomUse= document.createElementNS('http://www.w3.org/2000/svg', 'path');
            sortSvgBottomUse.setAttribute('d', 'M7.004 11.772l-6.765-6.418c-0.147-0.144-0.239-0.344-0.239-0.566s0.091-0.422 0.239-0.566l0-0c0.158-0.141 0.368-0.227 0.597-0.227s0.439 0.086 0.598 0.228l-0.001-0.001 6.367 6.038 6.767-6.038c0.158-0.141 0.368-0.227 0.597-0.227s0.439 0.086 0.598 0.228l-0.001-0.001c0.147 0.144 0.239 0.344 0.239 0.566s-0.091 0.422-0.239 0.566l-0 0-7.165 6.418c-0.16 0.145-0.374 0.233-0.608 0.233-0.039 0-0.078-0.003-0.117-0.007l0.005 0c-0.036 0.004-0.079 0.006-0.121 0.006-0.28 0-0.541-0.087-0.755-0.235l0.004 0.003z');
            sortSvgBottomUse.setAttribute('stroke-linecap', 'round');
            sortSvgBottom.appendChild(sortSvgBottomUse);
            this.sortDesc.classList.toggle('ag-hidden', sort !== 'desc');
            labelContainer.appendChild(this.sortDesc);
        }
        else {
            this.sortDesc = document.createElement('span');
            this.sortDesc.setAttribute('ref', 'eSortDesc');
            this.sortDesc.classList.add('ag-header-icon', 'ag-sort-descending-icon');
            this.sortDesc.classList.toggle('ag-hidden', sort !== 'desc');
            labelContainer.appendChild(this.sortDesc);

            const sortDescIcon: HTMLSpanElement = document.createElement('span');
            sortDescIcon.setAttribute('unselectable', 'on');
            sortDescIcon.classList.add('ag-icon', 'ag-icon-desc');
            this.sortDesc.appendChild(sortDescIcon);
        }
        this.onGlobalSortChanged = (e) => {
            const newSortOrderVal = this.getSortOrder(e.api, e.columnApi);
            sortOrder.classList.toggle('ag-hidden', newSortOrderVal <= 0);
            sortOrder.innerText = newSortOrderVal.toString();
        };

        this.onSortChanged = (e: ColumnEvent) => {
            const newSort = e.column.getSort();

            // update elements based on sort value
            this.sortAsc.classList.toggle('ag-hidden', newSort !== 'asc');
            this.sortDesc.classList.toggle('ag-hidden', newSort !== 'desc');
        };

        this.onFilterChanged = (e: ColumnEvent) => {
            filter.classList.toggle('ag-hidden', !e.column.isFilterActive());
        };


        params.column.addEventListener('sortChanged', this.onSortChanged);
        params.api.addEventListener('sortChanged', this.onGlobalSortChanged);
        params.column.addEventListener('filterChanged', this.onFilterChanged);
    }

    private setCxoneHeaderCell(moduleManager, labelText) {
        let editGroupName;
        if (!moduleManager.moduleSettings.selectedGroupName) {
            labelText.innerText = `${moduleManager.moduleSettings.selectedGroupLevelName}`;
            return;
        }
        if (moduleManager.moduleSettings.selectedGroupName.toLocaleLowerCase() === "all") { // all parents selected
            editGroupName = moduleManager.moduleSettings.selectedGroupLevel < moduleManager.moduleSettings.maxGroupLevel - 1 ?
                `${this.makePlural(moduleManager.moduleSettings.parentGroupLevelName)}: `
                + `${moduleManager.moduleSettings.selectedGroupName}` : '';
        }
        else if (!isNaN(moduleManager.moduleSettings.selectedGroupName[0] as any)) { // partial parents selected
            editGroupName = moduleManager.moduleSettings.selectedGroupName
                .replace(" ", " " + this.makePlural(moduleManager.moduleSettings.parentGroupLevelName) + " ");
        }
        else { // only one parent selected I.E: viewby agent and only one team is selected
            editGroupName = moduleManager.moduleSettings.parentGroupLevelName + ": "
                + moduleManager.moduleSettings.selectedGroupName;
        }

        labelText.innerText = `${moduleManager.moduleSettings.selectedGroupLevelName}`
            + (moduleManager.moduleSettings.selectedGroupLevel < moduleManager.moduleSettings.maxGroupLevel - 1 ?
                ` (${editGroupName})` : '');
    }

    private getSortOrder(api: GridApi, columnApi: ColumnApi) {
        // verify we are a sorted column
        if (this.column.getSort() == null)
            return 0;

        const colStates = columnApi.getColumnState();
        const sortedColumns = colStates.filter(col => col.sort != null);

        if (sortedColumns.length <= 1) // one or less items sorted, no need to display a number
            return 0;

        return this.column.getSortIndex() + 1;
    }

    private makePlural(singularNoun) {
        if (singularNoun.toLowerCase()[singularNoun.length - 1] == "s") {
            return singularNoun + "es";
        }
        if (singularNoun.toLowerCase()[singularNoun.length - 1] == "y") {
            return singularNoun.slice(0, singularNoun.length - 1) + "ies";
        }
        return singularNoun + "s";
    }

    refresh(params: IHeaderParams): boolean {
        return false;
    }

    afterGuiAttached?(params?: IAfterGuiAttachedParams): void {

    }

    destroy() {
        this.api.removeEventListener('sortChanged', this.onGlobalSortChanged);
        this.column.removeEventListener('sortChanged', this.onSortChanged);
        this.column.removeEventListener('filterChanged', this.onFilterChanged);
    }

    getGui(): HTMLElement {
        return this.gui;
    }
}
